import React, { useState } from 'react';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const num = typeof n === 'string' ? parseFloat(n.replace(/,/g, '')) : n;
  if (isNaN(num)) return String(n);
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000)    return (num / 1000).toFixed(0) + 'K';
  return String(num);
}

function borderColor(alert) {
  if (alert.models_fired === 'kmeans') return 'var(--blue)';
  if (alert.severity === 'high')       return 'var(--accent)';
  return 'var(--amber)';
}

function fmtShortDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateHeader(dateStr, todayStr) {
  const short = fmtShortDate(dateStr);
  return dateStr === todayStr ? `Today — ${short}` : short;
}

function ordinalLabel(n) {
  const suffixes = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th'];
  return suffixes[n] || `${n}th`;
}

function AlertCard({ alert, onClick, signalInfo }) {
  const isHigh    = alert.severity === 'high';
  const isKMeans  = alert.models_fired === 'kmeans';
  const pct       = Math.round(parseFloat(alert.anomaly_score) * 100);

  const hasRankNow  = alert.rank_now !== null && alert.rank_now !== undefined;
  const rankChange  = hasRankNow ? alert.rank - alert.rank_now : null;

  const wow     = parseFloat(alert.week_over_week_pct);
  const showWow = !isNaN(wow) && Math.abs(wow) >= 0.01;
  console.log(alert.name,
    'wow_pct raw:', alert.week_over_week_pct,
    'parsed:', parseFloat(alert.week_over_week_pct)
  );

  return (
    <div
      className="alert-card"
      onClick={() => onClick(alert)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderLeft: `3px solid ${borderColor(alert)}`,
        borderRadius: '10px', padding: '11px 14px', marginBottom: '6px',
        cursor: 'pointer', transition: 'transform 0.15s',
        display: 'flex', alignItems: 'center', gap: '12px',
        boxShadow: 'var(--card-shadow)',
        animation: 'cardSlideIn 0.3s ease forwards',
        animationDelay: 'var(--card-delay, 0ms)',
        opacity: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--card-shadow-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--card-shadow)'; }}
    >
      {/* Rank */}
      <div style={{ width: '52px', flexShrink: 0, textAlign: 'center' }}>
        <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>
          Flagged
        </div>
        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--muted)' }}>
          #{alert.rank}
        </div>
        {hasRankNow && (
          <>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '4px', marginBottom: '2px' }}>
              Now
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: rankChange > 0 ? 'var(--accent)' : rankChange < 0 ? 'var(--amber)' : 'var(--muted)' }}>
              #{alert.rank_now}
            </div>
          </>
        )}
      </div>

      {/* Track info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {alert.name}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '1px' }}>
          {alert.artist}
        </div>
        {showWow && (
          <div style={{ fontSize: '10px', color: wow >= 0 ? 'var(--accent)' : 'var(--amber)', marginTop: '2px' }}>
            {wow >= 0 ? '↑' : '↓'} {Math.abs(Math.round(wow * 100))}% playcount since last week
          </div>
        )}
        {signalInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '9px', padding: '2px 8px', borderRadius: '20px',
              background: 'var(--purple-bg)', color: 'var(--purple)',
              border: '1px solid var(--purple-bd)', whiteSpace: 'nowrap'
            }}>
              {signalInfo.ordinal === 2 ? `Flagged again — 2nd signal` : `${ordinalLabel(signalInfo.ordinal)} signal`}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
              Previously {signalInfo.prevDates.join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Pills */}
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {alert.both_models && (
          <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-bd)', whiteSpace: 'nowrap' }}>
            Both models
          </span>
        )}
        {isKMeans && (
          <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: 'var(--blue-bg)', color: 'var(--blue-text)', border: '1px solid var(--blue-bd)', whiteSpace: 'nowrap' }}>
            K-Means
          </span>
        )}
        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap',
          background: isHigh ? 'var(--accent-bg)' : 'var(--amber-bg)',
          color: isHigh ? 'var(--accent)' : 'var(--amber)',
          border: `1px solid ${isHigh ? 'var(--accent-bd)' : 'var(--border)'}`,
        }}>
          {isHigh ? 'High' : 'Medium'}
        </span>
        {hasRankNow && rankChange !== 0 && (
          <span style={{
            fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
            whiteSpace: 'nowrap',
            background: rankChange > 0 ? 'var(--accent-bg)' : 'var(--amber-bg)',
            color: rankChange > 0 ? 'var(--accent)' : 'var(--amber)',
            border: `1px solid ${rankChange > 0 ? 'var(--accent-bd)' : 'var(--amber-bd)'}`,
          }}>
            {rankChange > 0 ? `↑ ${rankChange} spots` : `↓ ${Math.abs(rankChange)} spots`}
          </span>
        )}
      </div>

      {/* Score */}
      <div style={{
        fontSize: '14px', fontWeight: 500, flexShrink: 0, width: '36px', textAlign: 'right',
        color: isHigh ? 'var(--accent)' : isKMeans ? 'var(--blue-text)' : 'var(--amber)'
      }}>
        {pct}%
      </div>
    </div>
  );
}

export default function AlertsList({ alerts, allAlerts = [], isDemo, onAlertClick }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = alerts.filter(a => {
    if (filter === 'high'   && a.severity !== 'high')          return false;
    if (filter === 'medium' && a.severity !== 'medium')        return false;
    if (filter === 'dual'   && !a.both_models)                 return false;
    if (filter === 'isolation_forest' && !a.models_fired.includes('isolation_forest')) return false;
    if (filter === 'kmeans'           && !a.models_fired.includes('kmeans'))           return false;
    if (search && !a.name.toLowerCase().includes(search) && !a.artist.toLowerCase().includes(search)) return false;
    return true;
  });

  // Build repeat-signal map using full historical alerts
  const trackDateMap = {};
  const historySource = allAlerts.length > 0 ? allAlerts : alerts;
  for (const a of historySource) {
    const key = a.track_id?.toLowerCase();
    if (!key) continue;
    if (!trackDateMap[key]) trackDateMap[key] = [];
    if (!trackDateMap[key].includes(a.detected_date)) {
      trackDateMap[key].push(a.detected_date);
    }
  }
  for (const id in trackDateMap) {
    trackDateMap[id].sort(); // ascending so index = occurrence order
  }

  function getSignalInfo(alert) {
    const dates = trackDateMap[alert.track_id?.toLowerCase()];
    if (!dates || dates.length <= 1) return null;
    const idx = dates.indexOf(alert.detected_date);
    if (idx <= 0) return null; // first occurrence — no badge
    return {
      ordinal: idx + 1,
      prevDates: dates.slice(0, idx).map(fmtShortDate),
    };
  }

  // Sort filtered descending by detected_date then group by date
  const sorted = [...filtered].sort((a, b) => b.detected_date.localeCompare(a.detected_date));

  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

  const groups = [];
  let currentDate = null;
  for (const a of sorted) {
    if (a.detected_date !== currentDate) {
      currentDate = a.detected_date;
      groups.push({ date: currentDate, items: [] });
    }
    groups[groups.length - 1].items.push(a);
  }

  const filterBtnStyle = (id) => ({
    fontSize: '12px', padding: '5px 14px', borderRadius: '20px', cursor: 'pointer',
    border: '1px solid var(--border)', transition: 'all 0.15s',
    background:  filter === id ? 'var(--accent-bg)' : 'var(--surface)',
    color:       filter === id ? 'var(--accent)'    : 'var(--muted)',
    borderColor: filter === id ? 'var(--accent-bd)' : 'var(--border)',
  });

  const FILTERS = [
    { id: 'all',    label: 'All' },
    { id: 'high',   label: 'High confidence' },
    { id: 'medium', label: 'Medium' },
    { id: 'dual',   label: 'Both models' },
    { id: 'isolation_forest',  label: 'Isolation Forest' },
    { id: 'kmeans', label: 'K-Means' },
  ];

  return (
    <div>
      {/* Demo banner */}
      {isDemo && (
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
          fontSize: '12px', color: 'var(--amber)'
        }}>
          Demo data — ML pipeline not yet active.
        </div>
      )}

    {/* Search */}
      <input
        className="alerts-search"
        placeholder="Search track or artist..."
        value={search}
        onChange={e => setSearch(e.target.value.toLowerCase())}
        style={{
          background: 'var(--input-bg)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '7px 12px', fontSize: '13px',
          color: 'var(--text)', width: '100%', outline: 'none',
          marginBottom: '8px', boxSizing: 'border-box'
        }}
      />

      {/* Filters — horizontally scrollable */}
      <div style={{
        display: 'flex', gap: '6px', marginBottom: '6px',
        overflowX: 'auto', paddingBottom: '2px',
        scrollbarWidth: 'none', msOverflowStyle: 'none'
      }}>
        {FILTERS.map(f => (
          <button key={f.id} style={{ ...filterBtnStyle(f.id), whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Window note */}
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '12px' }}>
        Showing alerts from the last 7 days · {filtered.length} result{filtered.length !== 1 ? 's' : ''}
      </div>

      {/* Cards grouped by date */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: 'var(--muted)',
          fontSize: '13px', background: 'var(--surface)',
          borderRadius: '10px', border: '1px solid var(--border)'
        }}>
          No alerts match your filter.
        </div>
      ) : (
        (() => { let cardIndex = 0; return groups.map(group => (
          <React.Fragment key={group.date}>
            {/* Date header */}
            <div style={{
              fontSize: '11px', fontWeight: 500, color: 'var(--sub)',
              padding: '10px 0 6px', display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              {fmtDateHeader(group.date, todayStr)}
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
            {group.items.map(a => {
              const delay = cardIndex * 80;
              cardIndex++;
              return (
                <div key={`${a.track_id}-${a.detected_date}`}
                     style={{'--card-delay': `${delay}ms`}}>
                  <AlertCard
                    alert={a}
                    onClick={onAlertClick}
                    signalInfo={getSignalInfo(a)}
                  />
                </div>
              );
            })}
          </React.Fragment>
        )); })()
      )}
    </div>
  );
}
